<template>
  <BaseForm class="h-70vh flex flex-col space-y-4" @submit="onSubmit">
    <div class="h-full flex-1 space-y-6 overflow-y-auto px-2">
      <!-- A storage booking has no arrival or balance (its ref is made for
           it): where the unit is and the customer's PIN instead. -->
      <section v-if="storageBooking" class="grid grid-cols-4 gap-4">
        <BaseInputField label="Location" class="col-span-4 sm:col-span-2">
          {{ storageBooking.location === "in" ? "In" : "Out" }}
          <span
            v-if="storageBooking.locationChangedAt"
            class="text-background-fg/50"
          >
            since {{ formatStorageDateTime(storageBooking.locationChangedAt) }}
          </span>
        </BaseInputField>
        <StoragePinField
          :booking-id="storageBooking.bookingId"
          :has-pin="storageBooking.hasPin"
          class="col-span-4 sm:col-span-2"
        />
        <BaseInputField
          v-if="storageBooking.touringBookingHere"
          label="Touring booking"
          class="col-span-4 sm:col-span-2"
        >
          <RouterLink
            :to="{
              name: 'bookingInstance',
              params: { bookingId: storageBooking.touringBookingHere.id },
            }"
            class="text-primary underline"
          >
            On site now{{
              storageBooking.touringBookingHere.pitchNumber
                ? `, pitch ${storageBooking.touringBookingHere.pitchNumber}`
                : ""
            }}
          </RouterLink>
        </BaseInputField>
      </section>

      <section v-else-if="!isStorage" class="grid grid-cols-4 gap-4">
        <BaseInputText
          v-if="!isEdit"
          v-model="form.bookingRef"
          label="Booking Reference"
          class="col-span-4 sm:col-span-2"
          :disabled="isEdit"
          name="bookingRef"
          :rules="rules.bookingRef"
        />
        <BaseInputField
          v-if="isEdit"
          label="Arrival Time"
          class="col-span-4 sm:col-span-2"
        >
          <template v-if="initialData?.arrivedAt">
            {{ formatDatetimeToDatetimeFull(parseISO(initialData.arrivedAt)) }}
          </template>
          <template v-else-if="initialData?.startDate">
            <span class="text-background-fg/50">
              Exp.
              {{
                initialData.arrivalStart
                  ? DateFns.format(
                      DateFns.parse(
                        initialData.arrivalStart,
                        "HH:mm:ss",
                        DateFns.parseISO(initialData.startDate),
                      ),
                      "dd/MM/yy h:mm a",
                    )
                  : "Unknown"
              }}
            </span>
          </template>
        </BaseInputField>
        <div class="col-span-4 sm:col-span-2">
          <div class="mb-3 flex gap-2">
            <div class="flex-1">
              <BaseInputCurrency
                v-model="form.balanceDue"
                label="Balance due"
                name="balanceDue"
              />
            </div>
            <button
              v-if="isEdit && !initialData?.arrivedAt"
              type="button"
              class="bg-secondary text-secondary-fg mt-6 whitespace-nowrap rounded-md px-4 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
              :disabled="isZeroBalanceAndArriving"
              @click="handleZeroBalanceAndArrive"
            >
              {{
                isZeroBalanceAndArriving
                  ? "Processing..."
                  : form.balanceDue > 0
                    ? "Zero & Arrive"
                    : "Arrive"
              }}
            </button>
          </div>
          <BaseInputToggle
            v-model="form.autoBalance"
            label="Pay Balance the Night Before Your Stay"
            name="autoBalance"
            :disabled="isEdit"
          />
        </div>
      </section>

      <!-- Times Section -->
      <section class="grid grid-cols-4 gap-4">
        <h3
          class="text-background-fg/87 border-background-fg/15 col-span-4 border-b pb-1 font-semibold"
        >
          {{ isStorage ? "Dates" : "Dates & Times" }}
        </h3>

        <BaseInputDate
          v-model="form.startDate"
          label="Start Date"
          class="col-span-4 sm:col-span-2"
          :rules="rules.startDate"
        />

        <BaseInputDate
          v-model="form.endDate"
          label="End Date"
          class="col-span-4 sm:col-span-2"
          :rules="rules.endDate"
        />
        <p
          v-if="isStorage"
          class="text-background-fg/60 col-span-4 -mt-2 text-xs sm:col-span-2 sm:col-start-3"
        >
          Entry stops after this date. Leaving is always allowed.
        </p>

        <BaseInputTimeDetailed
          v-if="!isStorage"
          v-model="form.arrivalTimeStart"
          :enabled="form.arrivalTimeStartEnabled"
          label="Override first arrival"
          class="col-span-2 lg:col-span-1"
          @update:enabled="(value) => (form.arrivalTimeStartEnabled = value)"
        />

        <BaseInputTimeDetailed
          v-if="!isStorage"
          v-model="form.arrivalTimeEnd"
          :enabled="form.arrivalTimeEndEnabled"
          label="Override last arrival"
          class="col-span-2 lg:col-span-1"
          :start="form.arrivalTimeStart"
          @update:enabled="(value) => (form.arrivalTimeEndEnabled = value)"
        />
      </section>

      <section class="grid grid-cols-4 gap-4">
        <h3
          class="text-background-fg/87 border-background-fg/15 col-span-4 border-b pb-1 font-semibold"
        >
          Site
        </h3>

        <BaseInputSelect
          v-model="form.siteId"
          :options="siteOptions"
          class="col-span-4 sm:col-span-2"
          label="Site"
          name="siteId"
          :rules="rules.siteId"
          :disabled="isEdit && isStorage"
        />

        <template v-if="isStorage">
          <BaseInputSelect
            v-model="storage.kind"
            :options="storageKindOptions"
            class="col-span-4 sm:col-span-2"
            label="Kind of unit"
            name="storageKind"
          />

          <BaseInputSelect
            v-model="storage.pitchId"
            :options="storagePitchOptions"
            class="col-span-4 sm:col-span-2"
            label="Storage pitch"
            name="storagePitchId"
            :disabled="!compound"
            :rules="rules.storagePitchId"
            hint="Free pitches, plus this booking's own."
          />
        </template>

        <template v-else-if="!isExperienceFreedomBooking">
          <BaseInputSelect
            v-model="form.pitchType"
            :options="pitchTypeOptions"
            class="col-span-4 sm:col-span-2"
            label="Pitch Type"
            name="pitchType"
            :disabled="!form.siteId"
            :rules="rules.pitchType"
          />

          <BaseInputText
            :model-value="form.pitchNumber ?? ''"
            label="Pitch"
            class="col-span-4 sm:col-span-2"
            name="pitchNumber"
            @update:model-value="(v) => (form.pitchNumber = v)"
          />

          <BaseInputText
            :model-value="form.reservedPitch ?? ''"
            label="Reserved Pitch"
            class="col-span-4 sm:col-span-2"
            name="reservedPitch"
            @update:model-value="(v) => (form.reservedPitch = v)"
          />
        </template>

        <template v-else>
          <BaseInputSelect
            v-model="form.pitchId"
            :options="experienceFreedomPitchOptions"
            class="col-span-4 sm:col-span-2"
            label="Unit"
            name="pitchId"
            :disabled="!form.siteId"
            :rules="rules.pitchId"
          />
        </template>

        <BaseInputSelect
          v-model="form.bookingType"
          class="col-span-2"
          :options="bookingTypeOptions"
          label="Booking Type"
          :disabled="isEdit && isStorage"
        />
      </section>

      <section>
        <h3 class="text-background-fg/87 pb-2 font-semibold">Customer</h3>
        <CustomerForm
          v-model:customer="form.customer"
          name="customerId"
          :allow-edit="!props.initialData?.id"
        />
      </section>

      <section class="grid grid-cols-4 gap-4">
        <h3
          class="text-background-fg/87 border-background-fg/15 col-span-4 border-b pb-1 font-semibold"
        >
          Vehicles
        </h3>

        <div class="col-span-4 sm:col-span-2">
          <BaseInputText
            v-model="form.primaryVehicleBooking.registration"
            :label="primaryRegistrationLabel"
            class="mb-3"
            name="vehicleBookings:0:registration"
            :rules="isStorage ? [required] : []"
            :hint="primaryRegistrationHint"
          />
          <DisallowListWarning
            class="mb-3"
            :registration="form.primaryVehicleBooking.registration"
          />
        </div>

        <BaseInputField
          label="Secondary Registrations"
          class="col-span-4 sm:col-span-2"
        >
          <template
            v-for="(vehicleBooking, index) in form.vehicleBookings"
            :key="vehicleBooking.id"
          >
            <BaseInputText
              v-model="vehicleBooking.registration"
              class="mb-3 w-full"
              :name="`vehicleBookings:${index + 1}:registration`"
            />
            <DisallowListWarning
              class="mb-3"
              :registration="vehicleBooking.registration"
            />
          </template>
          <template v-if="form.vehicleBookings.length < 2">
            <button
              type="button"
              class="ring-background-fg text-background-fg hover:bg-background-fg/10 w-full rounded py-2 ring-1"
              @click="addSecondaryregistration"
            >
              Add Registration
            </button>
          </template>
        </BaseInputField>
      </section>

      <section>
        <h3 class="text-background-fg/87 pb-2 font-semibold">Note</h3>
        <BaseInputTextarea v-model="form.note" name="note" />
      </section>

      <section v-if="!isStorage" class="space-y-4">
        <h3
          class="text-background-fg/87 border-background-fg/15 border-b pb-1 font-semibold"
        >
          Disallow
        </h3>
        <BaseInputToggle
          v-model="form.disallow"
          label="Disallow access to site"
          name="disallow"
        />
      </section>
    </div>

    <div
      class="grid w-full flex-shrink-0 grid-cols-4 items-center justify-end gap-4"
    >
      <button
        class="bg-background-fg/30 col-span-4 rounded-md px-4 py-2 text-sm text-white sm:col-span-1 sm:col-start-3"
        type="button"
        @click="onCancel"
      >
        Cancel
      </button>

      <button
        type="submit"
        class="bg-primary text-primary-fg col-span-4 rounded-md px-4 py-2 text-sm sm:col-span-1 sm:col-start-4"
      >
        Save Booking
      </button>
    </div>

    <template v-if="isSubmitting">
      <BaseContainerLoading />
    </template>
  </BaseForm>
</template>

<script setup lang="ts">
import { ApiError } from "@/api";
import { type FormSubmitHelpers } from "@/composables/useFormContext";
import { type BookingForm } from "@/services/bookings/bookingForm";
import { type Booking } from "@/services/bookings/bookings";
import { createStorageBooking } from "@/services/storage/createStorageBooking";
import { useStorageCompound } from "@/services/storage/getStorageCompound";
import {
  STORAGE_KIND_LABELS,
  formatStorageDateTime,
  type StorageBooking,
  type StorageBookingInput,
  type StorageKind,
} from "@/services/storage/storage";
import { updateStorageBooking } from "@/services/storage/updateStorageBooking";

const router = useRouter();

const { data: sites } = useSites();
const parseISO = DateFns.parseISO;
const props = defineProps<{
  initialData?: Booking | undefined;
  /** An existing storage booking's storage half: its location and PIN. */
  storageBooking?: StorageBooking | undefined;
}>();

const emit = defineEmits<{
  (event: "close"): void;
}>();

const { options: bookingTypeOptions } = useBookingTypes();

const form = getBookingFormState(props.initialData);

// Storage (Fast Storage): a storage booking at a site with a compound holds a
// compound pitch instead of a touring one, and is saved through the storage
// API, which issues the customer's PIN and texts their welcome. An existing
// booking is storage when it has an outfit; it can't be turned into one here.
const siteHasCompound = computed(
  () =>
    sites.value?.find((site) => site.id === form.siteId)?.hasStorageCompound ??
    false,
);
const isStorage = computed(() =>
  props.initialData?.id
    ? !!props.initialData.storage
    : form.bookingType === "storage" && siteHasCompound.value,
);
// A new storage booking runs a season by default: a year, ending the day
// before the anniversary.
watch(
  isStorage,
  (storageMode) => {
    if (!storageMode || props.initialData?.id || !form.startDate) return;
    form.endDate = DateFns.format(
      DateFns.addDays(
        DateFns.addYears(DateFns.parseISO(form.startDate), 1),
        -1,
      ),
      "yyyy-MM-dd",
    );
  },
  { immediate: true },
);
const storage = reactive({
  kind: (props.initialData?.storage?.kind ?? "caravan") as StorageKind,
  pitchId: props.initialData?.storage?.pitchId ?? "",
});
const { data: compound } = useStorageCompound(
  computed(() => (isStorage.value ? form.siteId : null)),
);
const storageKindOptions = (
  Object.keys(STORAGE_KIND_LABELS) as StorageKind[]
).map((value) => ({ value, label: STORAGE_KIND_LABELS[value] }));
const storagePitchOptions = computed(() => {
  const own = props.initialData?.storage;
  const options = (compound.value?.pitches ?? [])
    .filter(
      (pitch) =>
        !pitch.outfit ||
        pitch.id === own?.pitchId ||
        pitch.outfit.bookingId === props.initialData?.id,
    )
    .map((pitch) => ({ value: pitch.id, label: pitch.name }));
  // An ended booking is off the map, so its pitch may not be listed: keep it.
  if (own && !options.some(({ value }) => value === own.pitchId)) {
    options.unshift({ value: own.pitchId, label: own.pitchName });
  }
  return options;
});
const primaryRegistrationLabel = computed(() =>
  !isStorage.value
    ? "Primary Registration"
    : storage.kind === "motorhome"
      ? "Motorhome registration"
      : "Main vehicle registration",
);
const primaryRegistrationHint = computed(() =>
  !isStorage.value
    ? undefined
    : storage.kind === "motorhome"
      ? "The motorhome's own plate."
      : "Usually the car that tows the unit. Any of these plates opens the gates.",
);

// Storage errors that belong to one field.
const STORAGE_FIELD_FOR_CODE: Record<string, string> = {
  "storage-pitch-taken": "storagePitchId",
  "storage-wrong-compound": "storagePitchId",
  "storage-no-vehicles": "vehicleBookings:0:registration",
  "storage-too-many-vehicles": "vehicleBookings:0:registration",
};

const onSubmitStorage = async (helpers: FormSubmitHelpers) => {
  if (!form.customer) {
    toast.error("Choose or add the customer first.");
    return;
  }
  const input: StorageBookingInput = {
    customerId: form.customer.id,
    pitchId: storage.pitchId,
    kind: storage.kind,
    registrations: [form.primaryVehicleBooking, ...form.vehicleBookings]
      .map(({ registration }) => registration.trim())
      .filter(Boolean),
    startDate: form.startDate,
    endDate: form.endDate,
    note: form.note,
  };
  isSubmitting.value = true;
  try {
    if (props.initialData?.id) {
      await updateStorageBooking(props.initialData.id, input);
      toast.success("Storage booking saved");
      emit("close");
    } else {
      const created = await createStorageBooking(input);
      toast.success("Storage booking added");
      void router.replace({
        name: "bookingInstance",
        params: { bookingId: created.bookingId },
      });
    }
  } catch (error) {
    if (!(error instanceof ApiError)) throw error;
    toast.error(error.detail ?? "The storage booking was not saved.");
    const field = error.code ? STORAGE_FIELD_FOR_CODE[error.code] : undefined;
    if (field && error.detail) helpers.setErrors({ [field]: error.detail });
  } finally {
    isSubmitting.value = false;
  }
};

const addSecondaryregistration = () => {
  form.vehicleBookings = [
    ...form.vehicleBookings,
    {
      id: null,
      registration: "",
      isPrimary: false,
    },
  ];
};

const onSubmit = async (helpers: FormSubmitHelpers) => {
  if (isStorage.value) return onSubmitStorage(helpers);
  isSubmitting.value = true;

  try {
    const data: BookingForm = {
      ...form,
      // Clear pitch data if Experience Freedom booking
      pitchType: isExperienceFreedomBooking.value ? null : form.pitchType,
      pitchNumber: isExperienceFreedomBooking.value ? null : form.pitchNumber,
      reservedPitch: isExperienceFreedomBooking.value
        ? null
        : form.reservedPitch,
      // Set time values to null if disabled
      arrivalTimeStart: form.arrivalTimeStartEnabled
        ? form.arrivalTimeStart
        : null,
      arrivalTimeEnd: form.arrivalTimeEndEnabled ? form.arrivalTimeEnd : null,
      departureTimeEnd:
        hasExitCamera.value && form.departureTimeEndEnabled
          ? form.departureTimeEnd
          : null,
      // Filter out empty vehicle registrations
      vehicleBookings: form.vehicleBookings.filter((v) => !!v.registration),
    };

    if (props.initialData?.id) {
      await updateBooking(props.initialData?.id, data);
      toast.success("Booking updated");
      emit("close");
    } else {
      const response = await createBooking(data);
      toast.success("Booking created");
      router.push(response.id);
      emit("close");
    }
  } catch (error) {
    if (error instanceof ApiError) {
      if (isEdit.value) {
        toast.error("Failed to update booking");
      } else {
        toast.error("Failed to create booking");
      }

      if (error.requestErrors) {
        helpers.setErrors(error.requestErrors?.body ?? {});
      }
    }
  } finally {
    isSubmitting.value = false;
  }
};

const isEdit = computed(() => !!props.initialData?.id);

const isSubmitting = ref(false);
const isZeroBalanceAndArriving = ref(false);

const onCancel = () => emit("close");

const handleZeroBalanceAndArrive = async () => {
  if (!props.initialData?.id) return;

  isZeroBalanceAndArriving.value = true;

  try {
    // First, update the booking: zero balance and mark zero_and_arrive_used
    const updatedBookingData: BookingForm = {
      ...form,
      balanceDue: 0,
      zeroAndArriveUsed: true, // Mark that this button was used
      // Clear pitch data if Experience Freedom booking
      pitchType: isExperienceFreedomBooking.value ? null : form.pitchType,
      pitchNumber: isExperienceFreedomBooking.value ? null : form.pitchNumber,
      reservedPitch: isExperienceFreedomBooking.value
        ? null
        : form.reservedPitch,
      // Set time values to null if disabled
      arrivalTimeStart: form.arrivalTimeStartEnabled
        ? form.arrivalTimeStart
        : null,
      arrivalTimeEnd: form.arrivalTimeEndEnabled ? form.arrivalTimeEnd : null,
      departureTimeEnd:
        hasExitCamera.value && form.departureTimeEndEnabled
          ? form.departureTimeEnd
          : null,
      // Filter out empty vehicle registrations
      vehicleBookings: form.vehicleBookings.filter((v) => !!v.registration),
    };

    await updateBooking(props.initialData.id, updatedBookingData);
    toast.success("Balance updated to zero");

    // Then, arrive the booking (sets arrived_at and triggers SMS processes)
    await checkInBooking(props.initialData.id);
    toast.success("Booking arrived successfully");

    // Close the dialog
    emit("close");
  } catch (error) {
    if (error instanceof ApiError) {
      toast.error("Failed to update balance and arrive booking");
    }
  } finally {
    isZeroBalanceAndArriving.value = false;
  }
};

const siteOptions = computed(
  () =>
    sites.value?.map(({ id, name }) => ({
      value: id,
      label: name,
    })) || [],
);

const { data: pitchTypes } = usePitchTypes(
  computed(() => ({
    siteId: form.siteId,
  })),
);
const pitchTypeOptions = computed(
  () =>
    pitchTypes.value?.map(({ name }) => ({
      value: name,
      label: name,
    })) || [],
);

const { data: experienceFreedomPitches } = useKeycodePitches(
  computed(() => ({
    siteId: form.siteId,
    bookingType: "experience_freedom",
  })),
);

const experienceFreedomPitchOptions = computed(
  () =>
    experienceFreedomPitches.value?.map((pitch) => ({
      value: pitch.id,
      label: `${pitch.name} (${pitch.pitchTypeName})`,
    })) || [],
);

const isExperienceFreedomBooking = computed(
  () => form.bookingType === "experience_freedom",
);

const hasExitCamera = computed(() => {
  return (
    sites.value?.find((site) => site.id === form.siteId)?.hasExitCamera || false
  );
});

const required = (value: unknown) => !!value || "Field is required";

const rules = computed(() => ({
  bookingRef: isStorage.value ? [] : [required],
  startDate: [required],
  endDate: [required],
  siteId: [required],
  pitchType:
    !isExperienceFreedomBooking.value && !isStorage.value ? [required] : [],
  storagePitchId: isStorage.value ? [required] : [],
  pitchId: isExperienceFreedomBooking.value ? [required] : [],
}));
</script>
